import { Controller } from '@hotwired/stimulus'
import { Turbo } from "@hotwired/turbo-rails"
import { DateTime } from 'luxon'
import { AjaxModule } from 'tabulator-tables'
import DirectoryTable from '../lib/directory_table'
import { bindRowPreviewPrefetch } from '../lib/resource_preview_prefetch'
import TableScrollMemory from '../lib/table_scroll_memory'
import titleize from 'titleize'
import { ageColumn, isGroupRow, boardListMark, columnPersistenceOptions, stateBadge, columnVisibilityHeaderMenu, emptyStatePlaceholder, emptyStateFor, rowSelectionRowHeader } from './tabulator_helpers'

export default class extends Controller {
  boundDownloadCsv = this.downloadCsv.bind(this)
  static targets = ['processes', 'table', 'actions', 'replyAllMessageButton', 'changeStateAllButton', 'reassignAllButton', 'aiSelectButton', 'queryTitleBanner', 'queryTitleContent', 'searchSortMostRecent', 'searchSortBestMatch', 'loadError', 'loadRetry']

  static values = {
    url: String,
    agentName: String,
    agentKey: String,
    tabulatorTable: Object,
    metadataKeys: Array,
    hasRetries: Boolean,
    filtersOpen: Boolean,
    selectedRows: Array,
    userNameMap: Object,
    allAccessibleAgents: Array,
    hasDownloadQueryParam: Boolean,
    hasTokensQueryParam: Boolean,
    searchSortMode: String,
    lists: Array, // [{id, name, color, color_classes}]
    emptyState: Object, // {title, hint, action?: {label, url}} from workflow_list_empty_state
    groupSimilar: Boolean, // the person's "Group similar" view (User#group_similar_workflows)
  }

  // The rows arrow keys, ranges and the active highlight move over: the
  // visible workflow rows, with a group's members once it is open.
  navigableRows() {
    if (!this.tabulatorTable) return []
    const rows = this.tabulatorTable.getRows(this.groupSimilarValue ? 'display' : 'active')
    return this.groupSimilarValue ? rows.filter(row => !isGroupRow(row.getData())) : rows
  }

  // Every listed workflow row, a closed group's members included, and no
  // group row: what the CSV, AI Magic Select and the reference list hold.
  recordRows() {
    if (!this.tabulatorTable) return []
    const rows = this.tabulatorTable.getRows('active')
    if (!this.groupSimilarValue) return rows
    return rows.flatMap(row => isGroupRow(row.getData()) ? row.getTreeChildren() : [row])
  }

  // The footer of the grouped list. A group is one row, so "rows" alone would
  // undercount what select-all takes once groups are open; say how many
  // workflows stand behind the rows.
  groupedCounter(pageSize, currentRow, totalRows) {
    const shown = Math.max(Math.min(pageSize, totalRows - currentRow + 1), 0)
    const rows = `Showing ${shown} of ${totalRows} rows (a group is one row)`
    const workflows = this.groupedWorkflowCount
    if (!workflows) return rows
    const noun = workflows === 1 ? 'workflow' : 'workflows'
    // At the cap the list goes on: the server says so in the banner.
    return this.groupedWorkflowsCapped ? `${rows}, at least ${workflows} ${noun}` : `${rows}, ${workflows} ${noun} in all`
  }

  // Collapsing a group hides its members, so none stays selected: the action
  // bar never acts on rows the person cannot see.
  deselectHidden(parent) {
    this.suppressSelectionPreviewRefresh = true
    try {
      parent.getTreeChildren().forEach(child => child.deselect())
    } finally {
      this.suppressSelectionPreviewRefresh = false
    }
  }

  // Leaves a group's parent row out of selected or loaded data.
  recordsOnly(data) {
    return (data || []).filter(row => !isGroupRow(row))
  }

  // The selected workflows once a group's own row is swapped for the members
  // shown under it (none hidden in a closed group).
  groupedSelection(selected, data) {
    const parents = (selected || []).filter(row => isGroupRow(row.getData()))
    if (parents.length) {
      this.suppressSelectionPreviewRefresh = true
      try {
        parents.forEach(parent => {
          parent.deselect()
          if (parent.isTreeExpanded()) this.tabulatorTable.selectRow(parent.getTreeChildren())
        })
      } finally {
        this.suppressSelectionPreviewRefresh = false
      }
      data = this.tabulatorTable.getSelectedData()
    }
    return this.recordsOnly(data)
  }

  // A group's title: the count and who the workflows are about (the
  // contact, or the sender when there is no contact yet). The shared agent,
  // state and list sit in their own columns.
  groupTitle(group) {
    const wrapper = document.createElement('span')
    wrapper.className = 'wf-subject task-group-title'
    const count = document.createElement('span')
    count.className = 'task-group-title__count'
    count.textContent = `${group.count} workflows`
    const party = document.createElement('span')
    party.className = 'task-group-title__issue'
    party.textContent = group.contact || group.sender || 'Look alike'
    party.title = group.contact || (group.sender ? `${group.sender} (no contact yet: grouped by the sender of each workflow's first email)` : '')
    wrapper.append(count, party)
    return wrapper
  }

  // Turns "Group similar" on or off for this person, then reloads the list:
  // the data tree is a table option, so the table is built again.
  toggleGroupSimilar(event) {
    event?.preventDefault()
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch('/processes/group_similar', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ enabled: !this.groupSimilarValue })
    })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        Turbo.visit(window.location.href, { action: 'replace' })
      })
      .catch(error => console.error('Error saving the grouping preference:', error))
  }

  // Utility function to clean URL parameters
  cleanUrlParams(url) {
    const cleanUrl = new URL(url)
    cleanUrl.searchParams.delete('all_open')
    cleanUrl.searchParams.delete('all_on_hold')
    return cleanUrl
  }

  calculateTableHeight() {
    const headerElement = document.querySelector('.section-header-slim, .section-header')
    const filtersElement = document.querySelector('.section-filters')
    const mobileHeaderElement = document.querySelector('header.mobile') // Select the mobile-only header
    const queryTitleBannerElement = this.queryTitleBannerTarget

    // Calculate heights, checking for visibility
    const headerHeight = headerElement ? headerElement.offsetHeight : 0
    const filtersHeight = (filtersElement && window.getComputedStyle(filtersElement).display !== 'none') ? filtersElement.offsetHeight - 6 : 0
    const mobileHeaderHeight = (mobileHeaderElement && window.getComputedStyle(mobileHeaderElement).display !== 'none') ? mobileHeaderElement.offsetHeight : 0
    const queryTitleBannerHeight = (queryTitleBannerElement && !queryTitleBannerElement.classList.contains('hidden')) ? queryTitleBannerElement.offsetHeight : 0;

    // Combine all visible header heights
    const combinedHeight = mobileHeaderHeight + headerHeight + filtersHeight + queryTitleBannerHeight;
    return `max(240px, calc(100vh - ${combinedHeight}px))`
  }

  connect() {
    this._isRemoteSorting = false
    // Back to this list puts its scroll and open groups where they were. The
    // list always reloads on its first page, so a position saved on another
    // page is not restored.
    this.scrollMemory = new TableScrollMemory({ state: () => ({
      left: this.captureTableScrollLeft(),
      expanded: [...(this.expandedGroups || [])],
      page: this.tabulatorTable?.getPage?.(),
    }), onRestore: ({ left }) => this.restoreTableHorizontalScroll(left) })
    this.lastHorizontalScrollLeft = 0
    const expanded = this.scrollMemory.restored?.expanded
    if (expanded) this.expandedGroups = new Set(expanded)
    this.actionsTarget.style.display = 'none'
    this.selectedRowsValue = []
    this.activePreviewReference = null
    this.queryTitleBannerTarget.classList.add('hidden') // Ensure banner is hidden initially
    this.setLoadFailed(false)
    this.hasMultiplePages = false // Initialize pagination tracking

    const initialSearchSortMode = this.hasSearchSortModeValue ? this.searchSortModeValue : ''
    if ((this.hasSearchSortMostRecentTarget || this.hasSearchSortBestMatchTarget) && initialSearchSortMode === '') {
      this.originalSearchSortMode = 'recent'
    } else {
      this.originalSearchSortMode = initialSearchSortMode
    }
    this.updateSearchSortPillClasses(this.originalSearchSortMode || null)

    // Bind keyboard event handler
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeydown)

    this.boundHandleRefreshRequested = this.handleRefreshRequested.bind(this)
    window.addEventListener('process-table:refresh-requested', this.boundHandleRefreshRequested)

    this.boundUpdateSelectionActionsLayout = this.updateSelectionActionsLayout.bind(this)
    document.addEventListener('resource-preview:shown', this.boundUpdateSelectionActionsLayout)
    document.addEventListener('resource-preview:closed', this.boundUpdateSelectionActionsLayout)
    window.addEventListener('resize', this.boundUpdateSelectionActionsLayout)

    // Clear the active-row highlight when the preview drawer closes.
    this.boundClearActivePreviewRow = () => this.setActivePreviewRow(null)
    document.addEventListener('resource-preview:closed', this.boundClearActivePreviewRow)

    // Bulk actions dispatched from the multi-select preview drawer.
    this.boundHandleBulkAction = this.handleBulkAction.bind(this)
    document.addEventListener('process-table:bulk', this.boundHandleBulkAction)

    // A row of the selection drawer's list steps into its workflow.
    this.boundStepIntoFromPreview = (event) => this.stepIntoSelected(event.detail?.reference)
    document.addEventListener('process-table:step-into', this.boundStepIntoFromPreview)
    this.boundDeselectFromPreview = (event) => this.deselectSelected(event.detail?.reference)
    document.addEventListener('process-table:deselect', this.boundDeselectFromPreview)
    this.boundClearFromPreview = () => this.clearSelection()
    document.addEventListener('process-table:clear-selection', this.boundClearFromPreview)

    // List picked from the bulk assign-list quick-selector.
    this.boundBulkAssignList = this.bulkAssignList.bind(this)
    document.addEventListener('process-bulk-assign-list:select', this.boundBulkAssignList)

    // Re-layout when the table is revealed on mobile (the cards<->table toggle).
    // Tabulator was initialized while its container was display:none (mobile
    // shows cards by default), so its columns measured 0-width and pinned to the
    // 40px minimum — redraw alone won't recover that. On the first reveal,
    // auto-size each column to its content (setWidth(true)); afterwards just
    // redraw. This event only fires from the mobile toggle, so the desktop
    // table (already visible at init) is never touched.
    this.boundReveal = () => {
      if (!this.tabulatorTable) return
      if (!this.tableAutoSized) {
        this.tableAutoSized = true
        try { this.tabulatorTable.getColumns().forEach((c) => c.setWidth(true)) } catch (_) {}
      }
      this.tabulatorTable.redraw(true)
    }
    this.element.addEventListener('index-view:reveal', this.boundReveal)

    // Check if we have valid agent data for the Agent column
    const hasValidAgentData = this.allAccessibleAgentsValue &&
      this.allAccessibleAgentsValue.length > 0 &&
      this.allAccessibleAgentsValue.every(agent => agent.id && agent.name && agent.key)

    // Build JSON ajax URL regardless of page URL
    let ajaxUrl = this.urlValue
    try {
      const u = new URL(this.urlValue, window.location.origin)
      u.searchParams.set('format', 'json')
      ajaxUrl = u.toString()
    } catch (e) {}

    const buildTableLink = (href, text, className = 'link') => {
      const link = document.createElement('a')
      link.href = href
      link.className = `${className} process-table-truncate-link`
      link.setAttribute('data-turbo-prefetch', 'false')
      link.textContent = text == null ? '' : String(text)
      return link
    }

    // The status vocabulary is derived once, on PaperworkProcess#list_status, and
    // ships with each row. Re-deriving it here would be a second copy of the
    // precedence rules that could silently disagree with the mobile card list.
    const rowStatus = (data) => (data && data.list_status) || null

    const statusTitle = (data) => {
      const status = rowStatus(data)
      if (!status) return ''
      const parts = [`${status.label} — workflow state: ${data.state}`]
      if (data.agent_state) parts.push(`agent state: ${data.agent_state}`)
      return parts.join(', ')
    }

    // Just the attachment count, the way a mail client shows one. Extracted values
    // were tried here and were noise: an agent's extraction keys can hold URLs and
    // long free text, so the chips crowded the subject with things nobody could act
    // on. Those values keep their own sortable, exportable metadata columns.
    const buildAttachmentCount = (data) => {
      const count = Number(data.attachment_count) || 0
      if (count <= 0) return null

      const wrapper = document.createElement('span')
      wrapper.className = 'wf-attachments'
      wrapper.title = `${count} attachment${count === 1 ? '' : 's'}`

      const icon = document.createElement('i')
      icon.className = 'ti ti-paperclip'
      icon.setAttribute('aria-hidden', 'true')
      wrapper.appendChild(icon)

      const value = document.createElement('span')
      value.className = 'wf-attachments-count'
      value.textContent = String(count)
      wrapper.appendChild(value)

      return wrapper
    }

    // "Group similar": look-alike workflows nest under one parent row, closed
    // until opened. Same columns, so the saved layout still fits. A group the
    // person opened stays open when the data reloads.
    this.expandedGroups ||= new Set()
    const groupOptions = this.groupSimilarValue ? {
      dataTree: true,
      dataTreeStartExpanded: (row) => this.expandedGroups.has(row.getData().group?.key),
      dataTreeElementColumn: 'title',
      dataTreeChildIndent: 18,
      ajaxParams: { group: 'similar' },
    } : {}
    this.tableTarget.classList.toggle('process-table--grouped', this.groupSimilarValue)

    this.tabulatorTable = new DirectoryTable(this.tableTarget, {
      ...groupOptions,
      layout: 'fitDataFill',
      ...columnPersistenceOptions(`processes-table-v3-${this.agentKeyValue || 'all'}`),
      // Persist both columns and sort order in local storage for process list
      // so users keep their preferred layout and sort between reloads.
      persistence: { columns: ["width", "visible"], sort: true },
      columnDefaults: {
        minWidth: 90,
        headerMenu: columnVisibilityHeaderMenu,
      },
      // The server knows what emptied the list (a filter, or the page's own
      // Today default), so its copy wins; the address-only guess is the fallback.
      placeholder: this.emptyStateValue?.title
        ? () => emptyStatePlaceholder(this.emptyStateValue.title, this.emptyStateValue.hint, this.emptyStateValue.action)
        : emptyStateFor("workflows"),
      pagination: true,
      paginationCounter: this.groupSimilarValue ? (pageSize, currentRow, currentPage, totalRows) => this.groupedCounter(pageSize, currentRow, totalRows) : 'rows',
      paginationSize: 100,
      selectableRows: true,
      // Plain row clicks must not change the selection — they only open the
      // preview. Returning false here stops Tabulator from attaching its
      // click-to-select handlers; the row-header checkboxes still select rows
      // programmatically (which bypasses this check).
      selectableRowsCheck: () => false,
      rowHeader: rowSelectionRowHeader({
        onRangeSelect: (row) => this.selectRangeTo(row),
        rowSelectable: (row) => !isGroupRow(row.getData()),
      }),
      paginationMode: 'remote', // Enable remote pagination
      // paginationSizeSelector: true,
      // No "All rows" entry (Tabulator's `true`): it sends `length=true` and
      // asks the server for every workflow the filter matches in one response.
      paginationSizeSelector:[100, 250, 500, 1000],
      // paginationSizeSelector: true,
      // Calculate height dynamically based on header and filters
      height: this.calculateTableHeight(), // Use the helper method
      renderHorizontal: 'virtual',
      // Re-apply the active-row highlight whenever a row (re)renders so it
      // survives virtual scrolling and data refreshes (the previewed row, not
      // checkbox selection).
      rowFormatter: (row) => this.applyActiveStateToRow(row),
      sortMode: 'remote',
      dataSendParams: {
        page: 'page',
        size: 'length',
        sorters: 'sort',
      },
      ajaxURL: ajaxUrl,
      ajaxRequestFunc: this.buildAjaxRequest(),
      columns: [
        { title: 'ID', field: 'id', visible: false },
        {
          title: 'Reference',
          field: 'reference',
          width: 118,
          minWidth: 110,
          headerSort: 'remote', // Remote sorting
          frozen: false,
          // A group's row has no reference: it is not a workflow.
          formatter: (cell) => {
            const reference = cell.getValue()
            if (!reference) return ''
            const link = document.createElement('a')
            link.href = `/processes/${reference}`
            link.textContent = reference
            return link
          },
        },
        {
          title: 'Subject',
          field: 'title',
          minWidth: 220,
          widthGrow: 2,
          headerSort: 'remote', // Remote sorting
          formatter: (cell) => {
            const data = cell.getRow().getData()
            if (isGroupRow(data)) return this.groupTitle(data.group)
            const title = data.title || '(no subject)'

            const link = buildTableLink(`/processes/${data.reference}?back=1`, title, 'link font-bold')
            // A workflow show is a read-only destination; Turbo may prefetch
            // this one link after hover intent, unlike filter links.
            link.removeAttribute('data-turbo-prefetch')
            link.classList.remove('process-table-truncate-link')
            link.classList.add('wf-subject-label')

            const wrapper = document.createElement('span')
            wrapper.className = 'wf-subject'

            wrapper.appendChild(link)

            const attachments = buildAttachmentCount(data)
            if (attachments) wrapper.appendChild(attachments)

            return wrapper
          },
        },
        // How long the workflow has sat; the exact Created and Updated dates
        // are in its hover and stay in the column menu. The server sorts
        // "age" by created time.
        ageColumn({
          headerSort: 'remote',
          closed: (row) => ['completed', 'cancelled'].includes(row.state),
          closedAt: (row) => row.resolved_at || row.updated_at,
        }),
        {
          title: 'State',
          field: 'state',
          width: 108,
          minWidth: 96,
          headerSort: 'remote', // Remote sorting
          // Where the workflow stands (PaperworkProcess#list_status) as the
          // compact highlight; a routine open workflow keeps plain text so the
          // rows that need a look stand out. A click filters to the state.
          formatter: (cell) => {
            const data = cell.getRow().getData()
            const currentUrl = this.cleanUrlParams(window.location.href)
            currentUrl.searchParams.set('state', cell.getValue())
            const href = currentUrl.toString()
            return stateBadge(rowStatus(data), { href, title: statusTitle(data) })
              || buildTableLink(href, titleize(String(cell.getValue() || '').replace(/_/g, ' ')))
          },
        },
        {
          title: 'Board',
          field: 'paperwork_list_id',
          width: 150,
          minWidth: 120,
          // Shown by default only when the account has boards to show.
          visible: (this.listsValue || []).length > 0,
          headerSort: 'remote',
          formatter: (cell) => {
            const listId = cell.getValue()
            if (!listId) return '<span class="text-ink-soft text-sm">—</span>'
            
            const list = (this.listsValue || []).find(l => l.id === listId)
            if (!list) return '<span class="text-ink-soft text-sm">—</span>'
            
            const currentUrl = this.cleanUrlParams(window.location.href)
            currentUrl.searchParams.set('list_id', listId)

            return boardListMark(list, currentUrl.toString(), { compact: true })
          },
        },
        {
          title: 'Agent state',
          field: 'agent_state',
          width: 155,
          minWidth: 130,
          headerSort: 'remote', // Remote sorting
          formatter: (cell) => {
            // Members of one group can be at different agent steps.
            if (isGroupRow(cell.getRow().getData())) return ''
            const value = cell.getValue() || ''
            const currentUrl = this.cleanUrlParams(window.location.href)
            currentUrl.searchParams.set('agent_state', value)
            return buildTableLink(currentUrl.toString(), titleize(value.replace(/_/g, ' ')))
          },
        },
        ...(hasValidAgentData ? [{
          title: 'Agent',
          field: 'agent_id',
          minWidth: 180,
          sorter: (a, b, aRow, bRow, column, dir, sorterParams) => {
            // Get agent names for comparison
            const getAgentName = (agentId) => {
              if (!agentId) return ''
              const agent = this.allAccessibleAgentsValue.find(a => a.id === agentId)
              return agent ? agent.name : `Agent ID: ${agentId}`
            }

            const aAgentName = getAgentName(a)
            const bAgentName = getAgentName(b)

            return aAgentName.localeCompare(bAgentName)
          },
          formatter: (cell) => {
            const agentId = cell.getValue()
            if (!agentId) return ''

            // Find the agent by id
            const agent = this.allAccessibleAgentsValue.find(a => a.id === agentId)
            if (!agent) return `Agent ID: ${agentId}`

            const link = document.createElement('a')
            link.href = `/agents/${agent.key}/processes`
            link.className = 'inline-flex items-baseline gap-1 max-w-full min-w-0 no-underline process-table-truncate-link'
            link.setAttribute('data-turbo-prefetch', 'false')

            if (agent.icon) {
              const icon = document.createElement('span')
              icon.className = 'shrink-0 no-underline'
              icon.setAttribute('aria-hidden', 'true')
              icon.textContent = agent.icon
              link.appendChild(icon)
            }

            const name = document.createElement('span')
            name.className = 'min-w-0 truncate text-underline'
            name.textContent = agent.name
            link.appendChild(name)

            return link
          },
        }] : []),
        {
          title: 'Assigned to',
          field: 'assigned_user_id',
          minWidth: 180,
          headerSort: 'remote', // Remote sorting
          formatter: (cell) => {
            if (isGroupRow(cell.getRow().getData())) return ''
            const currentUrl = this.cleanUrlParams(window.location.href)
            currentUrl.searchParams.set('assigned_user_id', cell.getValue() || 'agent')
            const userId = cell.getValue()
            let userDisplayName = this.userNameMapValue[userId] || userId

            // If assigned to agent (no userId), try to find specific agent name
            if (!userId) {
              const agentId = cell.getRow().getData().agent_id
              if (agentId && this.allAccessibleAgentsValue) {
                const agent = this.allAccessibleAgentsValue.find(a => a.id === agentId)
                if (agent) {
                  userDisplayName = agent.name
                } else {
                  userDisplayName = this.agentNameValue || 'Agent'
                }
              } else {
                userDisplayName = this.agentNameValue || 'Agent'
              }
            }

            return buildTableLink(currentUrl.toString(), userDisplayName || 'Agent')
          },
        },
        {
          title: 'Created',
          field: 'created_at',
          width: 185,
          minWidth: 170,
          visible: false,
          headerSort: 'remote', // Remote sorting
          formatter: 'datetime',
          formatterParams: {
            inputFormat: 'iso',
            outputFormat: "MMM d, yy 'at' t",
            invalidPlaceholder: '(invalid date)',
          },
        },
        {
          title: 'Updated',
          field: 'updated_at',
          width: 185,
          minWidth: 170,
          visible: false,
          headerSort: 'remote', // Remote sorting
          formatter: 'datetime',
          formatterParams: {
            inputFormat: 'iso',
            outputFormat: "MMM d, yy 'at' t",
            invalidPlaceholder: '(invalid date)',
          },
        },
        ...(this.hasRetriesValue
          ? [
              {
                title: 'Retry at',
                field: 'retry_at',
                width: 185,
                minWidth: 170,
                formatter: 'datetime',
                formatterParams: {
                  inputFormat: 'iso',
                  outputFormat: "MMM d, yy 'at' t",
                  invalidPlaceholder: '-',
                },
              },
            ]
          : []),
        {
          title: 'Contacts',
          // field: 'contacts',
          field: 'cached_contact_role_references',
          minWidth: 180,          sorter: (a, b, aRow, bRow, column, dir, sorterParams) => {
            // Helper function to get the first display name from the contact object
            function getFirstDisplayName(contact) {
              if (!contact) return ''; // handle null/undefined
              const firstKey = Object.keys(contact)[0]; // get the first key
              return contact[firstKey] ? contact[firstKey].display_name : ''; // return display_name or empty string
            }

            // Extract the first display_name from the contact reference objects
            const aName = getFirstDisplayName(a);
            const bName = getFirstDisplayName(b);

            // Compare the display names
            if (aName > bName) {
              return 1;
            } else if (aName < bName) {
              return -1;
            } else {
              return 0;
            }
          },
          // sorter: 'array',
          // sorterParams: {
          //   type: 'string',
          //   alignEmptyValues: 'top',
          //   valueMap: 'display_name',
          // },
          formatter: (cell) => {
            const contactRoles = cell.getValue() || {}
            // { "Vendor" => { reference: "123", display_name: "Vendor Name" } }
            return Object.entries(contactRoles)
              .map(([_, contact]) => {
                if (!contact.reference) return null
                const currentUrl = this.cleanUrlParams(window.location.href)
                currentUrl.searchParams.set('contact', contact.reference)
                return `<a href="${currentUrl.toString()}" class="link" data-turbo-prefetch="false">${contact.display_name}</a>`
              })
              .filter((link) => link)
              .join(', ')
            // const contacts = cell.getValue() || []
            // return contacts
            //   .filter((contact) => contact.reference)
            //   .map((contact) => {
            //     const currentUrl = new URL(window.location.href)
            //     currentUrl.searchParams.set('contact', contact.reference)
            //     return `<a href="${currentUrl.toString()}" class="link">${
            //       contact.display_name
            //     }</a>`
            //   })
            //   .join(', ')
          },
        },
        ...this.metadataKeysValue.map((key) => ({
          title: key.split('.').pop(),
          field: key,
          minWidth: 160,
          formatter: (cell) => {
            const value = cell.getValue();
            if (value === null || value === undefined || value === '' || value === false) return '';
            if (Array.isArray(value) && value.length === 0) return '';

            // Extracted data is not always scalar: a collection key such as a
            // statement's line items arrives as an array of objects, which
            // stringifies to "[object Object]". Summarize structured values in
            // the cell and keep the full structure for the copy button.
            const isScalar = (val) => val === null || typeof val !== 'object';
            const summarize = (val) => {
              if (isScalar(val)) return val.toString();
              if (Array.isArray(val)) {
                if (val.every(isScalar)) return val.join(', ');
                return `${val.length} ${val.length === 1 ? 'item' : 'items'}`;
              }
              return JSON.stringify(val);
            };
            const displayValue = summarize(value);
            const copyValue = isScalar(value) ? value.toString() : JSON.stringify(value, null, 2);

            // Get the field key from the column definition
            const columnKey = cell.getColumn().getField();

            // Container for value and buttons
            const container = document.createElement('div');
            container.className = 'process-metadata-cell';

            // Value display element
            const valueSpan = document.createElement('span');
            valueSpan.className = 'process-metadata-value';

            // Check if it's a link based on the column key
            if (columnKey.endsWith('Link')) {
                const urlRegex = /(https?:\/\/[^\s]+)/g;
                const urls = displayValue.match(urlRegex);
                if (urls && urls.length > 0) {
                    const link = document.createElement('a');
                    link.href = urls[0];
                    link.className = "text-brand-primary hover:underline truncate block";
                    link.target = "_blank";
                    link.rel = "noopener noreferrer";
                    link.textContent = displayValue;
                    valueSpan.appendChild(link); // Append link to span
                } else {
                    valueSpan.textContent = displayValue; // Fallback to text if no URL found
                }
            } else {
                valueSpan.textContent = displayValue; // Display plain text
            }
            valueSpan.title = displayValue;

            // Button container for copy and search buttons
            const buttonContainer = document.createElement('div');
            buttonContainer.className = 'process-metadata-actions';

            // Copy button/icon
            const copyButton = document.createElement('button');
            // Heroicon: clipboard-document
            copyButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-secondary cursor-pointer"> <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" /> </svg>`;
            copyButton.className = "btn btn-circle btn-ghost btn-xs process-metadata-action";
            copyButton.title = "Copy to clipboard";
            copyButton.setAttribute('aria-label', 'Copy to clipboard');
            copyButton.onclick = (e) => {
                e.stopPropagation(); // Prevent row click or other events
                navigator.clipboard.writeText(copyValue).then(() => {
                    // Optional feedback: briefly change icon to checkmark
                    // Heroicon: check
                    copyButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-green-500"> <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" /> </svg>`;
                    setTimeout(() => {
                        // Restore original icon
                        copyButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-secondary cursor-pointer"> <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" /> </svg>`;
                    }, 1500); // Reset after 1.5 seconds
                }).catch(err => {
                    console.error('Failed to copy text: ', err);
                    // Optional: Provide error feedback (e.g., change icon to an error symbol)
                    copyButton.title = "Copy failed";
                });
            };

            // Check if value is worth searching for (exclude common base values)
            const isSearchable = (val) => {
              // Structured values have no useful full-text query.
              if (!isScalar(val)) return false;
              const stringVal = val.toString().toLowerCase().trim();
              // Exclude booleans
              if (stringVal === 'true' || stringVal === 'false') return false;
              // Exclude very short values (single characters or very short strings)
              if (stringVal.length <= 2) return false;
              // Exclude common single words that are too generic
              const genericWords = ['yes', 'no', 'null', 'none', 'n/a', 'na', 'tbd', 'tba'];
              if (genericWords.includes(stringVal)) return false;
              // Exclude simple numbers (but allow longer numbers that might be IDs)
              if (/^\d{1,3}$/.test(stringVal)) return false;
              return true;
            };

            buttonContainer.appendChild(copyButton);

            // Only add search button for searchable values
            if (isSearchable(value)) {
              // Search button/icon
              const searchButton = document.createElement('a');
              // Heroicon: magnifying-glass
              searchButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-secondary cursor-pointer">
                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>`;
              searchButton.className = "btn btn-circle btn-ghost btn-xs process-metadata-action";
              searchButton.title = "Search for this value";
              searchButton.setAttribute('aria-label', 'Search for this value');
              // Construct search URL with current URL but updated query parameter
              const searchUrl = this.cleanUrlParams(window.location.href);
              searchUrl.searchParams.set('query', displayValue);
              searchButton.href = searchUrl.toString();
              searchButton.setAttribute('data-turbo-prefetch', 'false');

              buttonContainer.appendChild(searchButton);
            }

            container.appendChild(valueSpan);
            container.appendChild(buttonContainer);

            return container; // Return the container element
          }
        })),
        // { title: "Open tasks", field: "open_tasks_count" },
        ...(this.hasTokensQueryParamValue
          ? [
              {
                title: 'AI Tokens Used',
                field: 'token_usage',
                width: 140,
                minWidth: 130,
                formatter: 'money',
                formatterParams: { thousand: ',', precision: false },
              },
            ]
          : []),
        ...(this.hasDownloadQueryParamValue
          ? [
              {
                title: 'Download',
                field: 'paperwork_download_urls',
                minWidth: 160,
                sorter: false, // Disable sorting for download links
                formatter: function(cell) {
                  const urls = cell.getValue() || {};
                  return Object.entries(urls)
                    .map(([name, url]) => {
                      return `<a href="${url}" class="link text-sm mr-1" download target="_blank">${name}</a>`;
                    })
                    .join(' ');
                },
              },
            ]
          : []),
      ],
      dependencies: {
        DateTime: DateTime,
      },
    })

    this.bindLoadFeedback()

    // #download_csv
    this.downloadButton = document.getElementById('download_csv')
    this.downloadButton?.addEventListener('click', this.boundDownloadCsv)

    if (this.groupSimilarValue) {
      this.tabulatorTable.on('dataTreeRowExpanded', (row) => this.expandedGroups.add(row.getData().group?.key))
      this.tabulatorTable.on('dataTreeRowCollapsed', (row) => {
        this.expandedGroups.delete(row.getData().group?.key)
        this.deselectHidden(row)
      })
    }

    bindRowPreviewPrefetch(this.tabulatorTable, {
      type: 'processes',
      isInteractive: target => this.isInteractiveElement(target),
      isGroup: isGroupRow,
      isSelectionPreview: row => (this.selectedRowsValue || []).length > 1 && row.isSelected(),
    })

    this.tabulatorTable.on('rowDblClick', (e, row) => {
      // Don't navigate when double-clicking the selection checkbox/gutter.
      if (e.target.closest('.tabulator-row-select')) return
      if (isGroupRow(row.getData())) return
      this.previewClickSequence = (this.previewClickSequence || 0) + 1
      Turbo.visit(`/processes/${row.getData().reference}?back=1`)
    })

    this.tabulatorTable.on('rowClick', (e, row) => {
      if (this.isInteractiveElement(e.target)) return
      // Clicks in the selection gutter toggle the checkbox (handled by the
      // column's cellClick); don't also handle the row here.
      if (e.target.closest('.tabulator-row-select')) return
      if (e.detail > 1) return
      // A group's row opens and closes its members.
      if (isGroupRow(row.getData())) {
        row.treeToggle()
        return
      }

      // Clicking a row only browses it — the checkbox gutter is the only thing
      // that selects. Double-click opens the workflow.
      this.deferPreviewRow(row)
    })

    this.tabulatorTable.on('rowSelectionChanged', (data, rows, selected) => {
      // Select-all takes the top-level rows, and a group's row stands for no
      // workflow. Selecting it selects the members shown under it, and none
      // hidden in a closed group, so the action bar never acts on rows the
      // person cannot see.
      data = this.groupedSelection(selected, data)
      this.selectedRowsValue = data
      // show and hide actions if present/absent
      this.updateSelectionActionsLayout()
      this.dispatch('selection-changed', {
        detail: {
          rows: data,
          ids: data.map(row => row.id).filter(id => id !== undefined && id !== null),
        },
        prefix: 'process-table'
      })

      // Checking a row's checkbox opens the preview for the just-checked process.
      // Programmatic selection changes (arrow-nav) set suppressSelectionPreviewRefresh
      // and are ignored here; deselecting everything leaves the drawer as-is.
      if (this.suppressSelectionPreviewRefresh) return
      if (data.length === 0) return

      const justSelected = selected && selected.length ? selected[selected.length - 1] : null
      let reference = justSelected ? justSelected.getData().reference : this.currentPreviewReference
      if (!reference || !data.some(process => process.reference === reference)) {
        reference = data[data.length - 1]?.reference
      }
      // Any checkbox selection — including a single row — opens the selection
      // drawer so the bulk action chips are available. (data.length === 0 has
      // already returned above; the chip-less browse preview is reached only by
      // clicking/arrowing to an unselected row.)
      this.openSelectionPreview(data, reference)
    })

    this.scrollMemory.attach(this.tabulatorTable)

    // With remote sort mode, Tabulator handles passing sorters via AJAX
    this.tabulatorTable.on('scrollHorizontal', (left) => {
      this.lastHorizontalScrollLeft = left || 0
    })

    // NOTE: we intentionally do NOT redraw the table after a sort/data load.
    // Under the fitDataFill layout, redraw() (and a synthetic window resize)
    // re-runs column layout and snaps every column back to its definition
    // width — discarding both widths the user has dragged AND the persisted
    // layout (the first dataLoaded redraw would otherwise wipe restored widths
    // on every page load, which is why saved widths never "stuck"). Remote sort
    // and pagination reload and re-render the rows on their own, so no manual
    // redraw is needed to keep cells aligned.
    this.tabulatorTable.on('dataSorting', () => {
      this.lastHorizontalScrollLeft = this.captureTableScrollLeft()
    })

    // Clear any column sort when search_sort mode is active
    // This ensures the search_sort preference (Most Recent/Best Match) takes precedence
    const table = this.tabulatorTable
    table.on('tableBuilt', () => {
      if (table.destroyed) return
      if (this.searchSortModeValue) {
        // Visually clear sort arrows without triggering a redundant remote data fetch.
        // clearSort() with sortMode:'remote' fires a second AJAX request, so we
        // reset header indicators directly instead.
        table.getColumns().forEach(col => {
          col.getElement().setAttribute('aria-sort', 'none')
        })
      }
    })

    // Store process references in local storage when data is loaded
    this.tabulatorTable.on('dataLoaded', (data) => {
      console.log('dataLoaded', data)
      // clear any pending remote sort guard after fresh data
      this._isRemoteSorting = false
      try {
        // Extract references from the loaded data (a group's members, not its row)
        const references = data.flatMap(process => isGroupRow(process) ? (process._children || []) : [process]).map(process => process.reference)

        // Store in local storage with a meaningful key
        localStorage.setItem('paperworkProcessReferences', JSON.stringify(references))

        console.log(`Stored ${references.length} process references in local storage`)
      } catch (error) {
        console.error('Error storing process references in local storage:', error)
      }

      this.restoreTableHorizontalScroll()
    })

    this.tabulatorTable.on('renderComplete', () => {
      this.restoreTableHorizontalScroll()
    })
  }

  captureTableScrollLeft() {
    if (!this.tabulatorTable) return this.lastHorizontalScrollLeft || 0

    const rowElement = this.tabulatorTable.rowManager?.getElement?.()
    const headerElement = this.tabulatorTable.columnManager?.getContentsElement?.()

    return Math.max(
      rowElement?.scrollLeft || 0,
      headerElement?.scrollLeft || 0,
      this.lastHorizontalScrollLeft || 0
    )
  }

  setTableHeightPreservingHorizontalScroll(scrollLeft = this.captureTableScrollLeft()) {
    if (!this.tabulatorTable) return

    const height = this.calculateTableHeight()
    if (this.tabulatorTable.options?.height !== height) {
      this.tabulatorTable.setHeight(height)
    }

    this.restoreTableHorizontalScroll(scrollLeft)
  }

  restoreTableHorizontalScroll(scrollLeft = this.lastHorizontalScrollLeft) {
    if (!this.tabulatorTable || !scrollLeft) return

    const restore = () => {
      try {
        this.tabulatorTable.rowManager?.scrollHorizontal?.(scrollLeft)
        this.tabulatorTable.columnManager?.scrollHorizontal?.(scrollLeft)
        this.lastHorizontalScrollLeft = scrollLeft
      } catch (_) {}
    }

    restore()
    requestAnimationFrame(restore)
  }

  updateSearchSortPillClasses(mode) {
    if (!this.hasSearchSortMostRecentTarget && !this.hasSearchSortBestMatchTarget) return

    if (this.hasSearchSortMostRecentTarget) {
      this.searchSortMostRecentTarget.classList.remove('tab-active')
    }

    if (this.hasSearchSortBestMatchTarget) {
      this.searchSortBestMatchTarget.classList.remove('tab-active')
    }

    if (mode === 'recent' && this.hasSearchSortMostRecentTarget) {
      this.searchSortMostRecentTarget.classList.add('tab-active')
    } else if (mode === 'match' && this.hasSearchSortBestMatchTarget) {
      this.searchSortBestMatchTarget.classList.add('tab-active')
    }
  }

  downloadCsv() {
    const filename = `${this.agentKeyValue}-paperwork-${DateTime.now().toFormat('yyyy-MM-dd-HH-mm')}.csv`
    // Grouped, the CSV is every listed workflow once, closed groups' members
    // included, and no group rows.
    const range = this.groupSimilarValue ? () => this.recordRows() : undefined
    this.tabulatorTable?.download('csv', filename, {}, range)
  }

  disconnect() {
    this.loadRequestSequence = (this.loadRequestSequence || 0) + 1
    this.downloadButton?.removeEventListener('click', this.boundDownloadCsv)
    this.downloadButton = null
    // Clean up keyboard event listener
    if (this.boundHandleKeydown) {
      document.removeEventListener('keydown', this.boundHandleKeydown)
    }
    if (this.boundHandleRefreshRequested) {
      window.removeEventListener('process-table:refresh-requested', this.boundHandleRefreshRequested)
    }
    if (this.boundUpdateSelectionActionsLayout) {
      document.removeEventListener('resource-preview:shown', this.boundUpdateSelectionActionsLayout)
      document.removeEventListener('resource-preview:closed', this.boundUpdateSelectionActionsLayout)
      window.removeEventListener('resize', this.boundUpdateSelectionActionsLayout)
    }
    if (this.boundClearActivePreviewRow) {
      document.removeEventListener('resource-preview:closed', this.boundClearActivePreviewRow)
    }
    if (this.boundHandleBulkAction) {
      document.removeEventListener('process-table:bulk', this.boundHandleBulkAction)
    }
    if (this.boundStepIntoFromPreview) {
      document.removeEventListener('process-table:step-into', this.boundStepIntoFromPreview)
    }
    if (this.boundDeselectFromPreview) {
      document.removeEventListener('process-table:deselect', this.boundDeselectFromPreview)
      document.removeEventListener('process-table:clear-selection', this.boundClearFromPreview)
    }
    if (this.boundBulkAssignList) {
      document.removeEventListener('process-bulk-assign-list:select', this.boundBulkAssignList)
    }
    if (this.boundReveal) {
      this.element.removeEventListener('index-view:reveal', this.boundReveal)
    }
    this.scrollMemory?.detach()
    this.scrollMemory = null
    this.tabulatorTable?.destroy()
    this.tabulatorTable = null
  }

  buildAjaxRequest() {
    const controller = this
    return function(url, config, params) {
      const sequence = controller.loadRequestSequence = (controller.loadRequestSequence || 0) + 1
      return AjaxModule.defaultLoaderPromise.call(this, url, config, params)
        .then(response => controller.processAjaxResponse(response, sequence))
        .catch(cause => {
          const error = new Error('Workflows could not be loaded', { cause })
          error.workflowLoadSequence = sequence
          throw error
        })
    }
  }

  processAjaxResponse(response, sequence) {
    if (!response || !Array.isArray(response.processes)) throw new Error('Invalid workflow response')

    if (sequence === this.loadRequestSequence) {
      const scrollLeft = this.captureTableScrollLeft()
      const bannerText = [response.query_title, response.search_notice].filter(Boolean).join(' - ')
      if (bannerText) {
        this.queryTitleContentTarget.textContent = bannerText
        this.queryTitleBannerTarget.classList.remove('hidden')
      } else {
        this.queryTitleBannerTarget.classList.add('hidden')
      }
      this.setTableHeightPreservingHorizontalScroll(scrollLeft)
      this.hasMultiplePages = response.last_page > 1
      this.groupedWorkflowCount = response.workflows
      this.groupedWorkflowsCapped = response.workflows_capped === true
    }

    return { data: response.processes, last_page: response.last_page, last_row: response.last_row }
  }

  bindLoadFeedback() {
    // Tabulator clears its error overlay after a few seconds and also emits
    // failures for superseded requests. Only persist the current failure.
    this.tabulatorTable.on('dataLoadError', error => {
      if (error?.workflowLoadSequence === this.loadRequestSequence) this.setLoadFailed(true)
    })
    this.tabulatorTable.on('dataLoaded', () => this.setLoadFailed(false))
  }

  setLoadFailed(failed) {
    this.loadErrorTarget.classList.toggle('hidden', !failed)
    this.tableTarget.classList.toggle('invisible', failed)
    this.loadRetryTarget.disabled = false
  }

  retryLoad() {
    this.loadRetryTarget.disabled = true
    this.tabulatorTable.setPage(this.tabulatorTable.getPage())
  }

  handleRefreshRequested(event) {
    if (!this.tabulatorTable) return

    try {
      this.tabulatorTable.replaceData()
      this.tabulatorTable.deselectRow()
    } catch (error) {
      console.error('Unable to refresh table data:', error)
    }

    this.selectedRowsValue = []
    if (this.hasActionsTarget) {
      this.actionsTarget.style.display = 'none'
    }
  }

  handleKeydown(event) {
    if (this.hasOpenModal()) return
    if (this.handlePreviewNavigation(event)) return

    // Handle Escape key to clear selection
    if (event.key === 'Escape') {
      // Don't handle if we're in an input field (let the input handle escape)
      if (this.isInInputField(event.target)) return

      // Only clear selection if there are selected rows
      if (this.selectedRowsValue.length > 0) {
        event.preventDefault()
        this.clearSelection()
        return
      }
    }

    // Handle Alt + Shift combinations for direct state changes
    if (event.altKey && event.shiftKey) {
      // Don't handle if we're in an input field
      if (this.isInInputField(event.target)) return

      // Alt + Shift + O: Set selected rows to Open
      if (event.code.toLowerCase() === 'keyo') {
        event.preventDefault()
        this.setSelectedRowsState('open')
        return
      }

      // Alt + Shift + H: Set selected rows to On Hold
      if (event.code.toLowerCase() === 'keyh') {
        event.preventDefault()
        this.setSelectedRowsState('on_hold')
        return
      }

      // Alt + Shift + C: Set selected rows to Completed
      if (event.code.toLowerCase() === 'keyc') {
        event.preventDefault()
        this.setSelectedRowsState('completed')
        return
      }

      // Alt + Shift + X: Set selected rows to Cancelled
      if (event.code.toLowerCase() === 'keyx') {
        event.preventDefault()
        this.setSelectedRowsState('cancelled')
        return
      }

      // Alt + Shift + T: Review tasks from selected processes
      if (event.code.toLowerCase() === 'keyt') {
        event.preventDefault()
        this.viewTasksFromProcesses()
        return
      }
    }
  }

  isInInputField(element) {
    const tagName = element?.tagName?.toLowerCase()
    if (!tagName) return false
    if (tagName === 'textarea' || tagName === 'select') return true
    if (element.isContentEditable) return true
    if (tagName === 'input') {
      // Only text-entry inputs should swallow keys like Escape/arrows. A focused
      // selection checkbox must not block Escape-to-deselect or arrow nav.
      const type = (element.getAttribute('type') || 'text').toLowerCase()
      return !['checkbox', 'radio', 'button', 'submit', 'reset', 'file'].includes(type)
    }
    return false
  }

  isInteractiveElement(element) {
    if (!element) return false

    return !!element.closest(
      'a, button, input, select, textarea, summary, [role="button"], [data-no-resource-preview], turbo-frame, .btn, .link',
    )
  }

  isResourcePreviewOpen() {
    const panel = document.querySelector('[data-resource-preview-target="panel"]')
    return panel?.dataset.open === "true"
  }

  hasOpenModal() {
    return Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"], [role="dialog"]'))
      .some((element) => !element.classList.contains("hidden") && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden")
  }

  handlePreviewNavigation(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return false
    if (!this.isResourcePreviewOpen()) return false
    if (this.isInInputField(event.target)) return false
    if (event.altKey || event.ctrlKey || event.metaKey) return false

    const direction = event.key === 'ArrowDown' ? 1 : -1
    event.preventDefault()

    // Plain arrow: same rule as a row click — move the preview without touching
    // the selection.
    const row = this.previewNavigationRow(direction)
    if (!row) return true

    this.previewRow(row)
    this.scrollRowIntoView(row)
    return true
  }

  previewNavigationRow(direction) {
    if (!this.tabulatorTable) return null

    const activeRows = this.navigableRows()
    if (activeRows.length === 0) return null

    const currentReference = this.currentPreviewReference || this.selectedRowsValue[0]?.reference
    const currentIndex = activeRows.findIndex(row => row.getData().reference === currentReference)
    const fallbackIndex = direction > 0 ? -1 : activeRows.length
    const nextIndex = Math.max(0, Math.min(activeRows.length - 1, (currentIndex >= 0 ? currentIndex : fallbackIndex) + direction))

    if (currentIndex === nextIndex) return null

    return activeRows[nextIndex]
  }

  scrollRowIntoView(row) {
    const rowElement = row.getElement()
    if (!rowElement) return

    const tableRect = this.tableTarget.getBoundingClientRect()
    const rowRect = rowElement.getBoundingClientRect()
    const isVisible = rowRect.top >= tableRect.top && rowRect.bottom <= tableRect.bottom

    if (!isVisible) {
      rowElement.scrollIntoView({ block: "nearest" })
    }
  }

  updateSelectionActionsLayout() {
    if (!this.hasActionsTarget) return

    const hasSelection = this.selectedRowsValue.length > 0
    const previewOpen = this.isResourcePreviewOpen()
    const desktopPreview = window.matchMedia('(min-width: 1280px)').matches

    this.actionsTarget.style.display = hasSelection ? 'flex' : 'none'
    this.actionsTarget.style.right = previewOpen && desktopPreview ? 'calc(32rem + 1rem)' : '1rem'
  }

  openPreviewForRow(row, options = {}) {
    const process = row.getData()
    if (!process.reference) return

    if (options.selectRow) {
      this.suppressSelectionPreviewRefresh = true
      try {
        this.tabulatorTable.deselectRow()
        row.select()
      } finally {
        this.suppressSelectionPreviewRefresh = false
      }
      this.selectedRowsValue = this.recordsOnly(this.tabulatorTable.getSelectedData())
    }

    this.currentPreviewReference = process.reference
    this.setActivePreviewRow(process.reference)

    document.dispatchEvent(
      new CustomEvent("resource-preview:open", {
        detail: {
          type: "process",
          id: process.reference,
          title: process.title || process.reference,
          url: `/processes/${process.reference}/preview`,
        },
      }),
    )
  }

  // Multi-select drawer: a "N workflows selected" summary with the selected list
  // and the current workflow's preview, mirroring the tasks selection drawer.
  // `step` opens one workflow inside the drawer (browsing a selected row, arrow
  // keys); otherwise the drawer lists the selection under its bulk actions.
  openSelectionPreview(processes, currentReference, { step = false } = {}) {
    const references = processes.map(process => process.reference).filter(Boolean)
    if (references.length === 0) return

    // Keep the navigation anchor in sync with the current row so arrow-key
    // navigation steps from the right place within a multi-selection; its
    // ring marks where the arrow keys go on from.
    if (currentReference) this.currentPreviewReference = currentReference
    this.setActivePreviewRow(currentReference)

    const params = new URLSearchParams()
    references.forEach(reference => params.append("references[]", reference))
    if (step && currentReference) params.set("current_reference", currentReference)

    document.dispatchEvent(
      new CustomEvent("resource-preview:open", {
        detail: {
          type: "process-selection",
          id: references.join(","),
          title: `${references.length} ${references.length === 1 ? "workflow" : "workflows"} selected`,
          url: `/processes/selection_preview?${params.toString()}`,
        },
      }),
    )
  }

  stepIntoSelected(reference) {
    if (!reference || !this.selectedRowsValue.some(process => process.reference === reference)) return
    this.openSelectionPreview(this.selectedRowsValue, reference, { step: true })
  }

  // Unchecking a row in the drawer: the table's own selection change redraws
  // the drawer for what is left (the list, one task's preview, or nothing).
  deselectSelected(reference) {
    const row = this.recordRows().find(candidate => candidate.getData().reference === reference)
    if (row?.isSelected()) row.deselect()
  }

  clearSelection() {
    // Clear selection in Tabulator table
    this.tabulatorTable.deselectRow()

    // Update the selectedRowsValue to empty array
    this.selectedRowsValue = []

    // Hide the actions panel since no rows are selected
    this.updateSelectionActionsLayout()
  }

  // Browsing a row via a plain click or arrow key: move the preview to it and
  // leave the checkbox selection alone. Rows are only ever selected from the
  // checkbox gutter, so browsing never builds a selection. A selected row keeps
  // the selection drawer open and just becomes its current row.
  previewRow(row) {
    if (row.isSelected()) {
      this.makeCurrentPreviewRow(row)
    } else {
      this.openPreviewForRow(row)
    }
  }

  deferPreviewRow(row) {
    const table = this.tabulatorTable
    const sequence = this.previewClickSequence = (this.previewClickSequence || 0) + 1
    setTimeout(() => {
      if (sequence === this.previewClickSequence && this.tabulatorTable === table && table && !table.destroyed) this.previewRow(row)
    })
  }

  // Shift+click in the checkbox gutter: select every row from the current row
  // (anchor) to this one, inclusive. Additive — never deselects.
  selectRangeTo(row) {
    const activeRows = this.navigableRows()
    const anchorReference = this.currentPreviewReference || this.selectedRowsValue[this.selectedRowsValue.length - 1]?.reference
    const anchorIndex = activeRows.findIndex(activeRow => activeRow.getData().reference === anchorReference)
    const targetIndex = activeRows.findIndex(activeRow => activeRow.getData().reference === row.getData().reference)

    if (anchorIndex === -1 || targetIndex === -1) {
      if (!row.isSelected()) row.select()
      return
    }

    const [from, to] = anchorIndex <= targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex]
    const rowsInRange = activeRows.slice(from, to + 1)

    this.suppressSelectionPreviewRefresh = true
    try {
      rowsInRange.forEach(rangeRow => rangeRow.select())
    } finally {
      this.suppressSelectionPreviewRefresh = false
    }

    // Shift-clicking highlights text in the browser — clear it.
    window.getSelection()?.removeAllRanges()

    this.selectedRowsValue = this.recordsOnly(this.tabulatorTable.getSelectedData())
    this.currentPreviewReference = row.getData().reference
    if (this.selectedRowsValue.length >= 1) {
      this.openSelectionPreview(this.selectedRowsValue, this.currentPreviewReference)
    } else {
      this.openPreviewForRow(row)
    }
  }

  // Move the current/previewed row to an already-selected row without changing
  // the selection (used when arrowing back through a multi-selection).
  makeCurrentPreviewRow(row) {
    this.currentPreviewReference = row.getData().reference
    this.setActivePreviewRow(this.currentPreviewReference)
    if (this.selectedRowsValue.length >= 1) {
      this.openSelectionPreview(this.selectedRowsValue, this.currentPreviewReference, { step: true })
    } else {
      this.openPreviewForRow(row)
    }
  }

  // Highlight the row whose preview drawer is open — the "current" row. Separate
  // from checkbox selection: shown as a ring (.tabulator-row-active) so it reads
  // distinctly from the selected (dark) fill, including on a selected row.
  setActivePreviewRow(reference) {
    this.activePreviewReference = reference || null
    if (!this.tabulatorTable) return
    const rows = this.groupSimilarValue ? this.tabulatorTable.getRows('display') : this.tabulatorTable.getRows()
    rows.forEach((row) => this.applyActiveStateToRow(row))
  }

  applyActiveStateToRow(row) {
    const el = row.getElement()
    if (!el) return
    el.classList.toggle(
      "tabulator-row-active",
      !!this.activePreviewReference && row.getData().reference === this.activePreviewReference,
    )
  }

  setSelectedRowsState(state) {
    if (this.selectedRowsValue.length === 0) {
      // If no rows selected, do nothing or show a message
      return
    }

    // Prevent rapid multiple submissions
    if (this.isSubmitting) {
      return
    }

    this.isSubmitting = true

    // Create form data for batch state update
    const formData = new FormData()
    formData.append('state', state)
    // Add each ID as a separate ids[] parameter
    this.selectedRowsValue.forEach(row => {
      formData.append('ids[]', row.id)
    })

    // Use the existing change_state_all endpoint
    fetch('/processes/change_state_all', {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.status === 'success') {
        // Refresh the table data
        this.tabulatorTable.replaceData()
        // Clear selection
        this.tabulatorTable.deselectRow()

        // Show visual feedback
        if (data.message) {
          console.log(data.message)
        }
      } else {
        console.error('Failed to update states:', data.message || 'Unknown error')
      }
    })
    .catch(error => {
      console.error('Error updating states:', error)
    })
    .finally(() => {
      setTimeout(() => {
        this.isSubmitting = false
      }, 1000)
    })
  }

  changeStateAllProcesses() {
    console.log('Changing state for processes:', this.selectedRowsValue)
    const baseUrl = this.changeStateAllButtonTarget.href.split('?')[0];
    const newUrl = baseUrl + '?' + this.selectedRowsValue.map(row => `ids[]=${row.id}`).join('&');
    this.changeStateAllButtonTarget.href = newUrl;
    this.changeStateAllButtonTarget.click();
  }

  replyAllProcesses() {
    console.log('Retrying processes:', this.selectedRowsValue)
    const baseUrl = this.replyAllMessageButtonTarget.href.split('?')[0];
    const newUrl = baseUrl + '?' + this.selectedRowsValue.map(row => `ids[]=${row.id}`).join('&');
    this.replyAllMessageButtonTarget.href = newUrl;
    this.replyAllMessageButtonTarget.click()
  }

  reassignAllProcesses() {
    console.log('Reassigning processes:', this.selectedRowsValue)
    const baseUrl = this.reassignAllButtonTarget.href.split('?')[0];
    const newUrl = baseUrl + '?' + this.selectedRowsValue.map(row => `ids[]=${row.id}`).join('&');
    this.reassignAllButtonTarget.href = newUrl;
    this.reassignAllButtonTarget.click()
  }

  aiMagicSelect() {
    // Get all visible processes from the table
    const allVisibleProcesses = this.recordRows().map(row => row.getData())

    if (allVisibleProcesses.length === 0) {
      alert('No processes found')
      return
    }

    console.log('AI Magic Select for all visible processes:', allVisibleProcesses)

    // Create form data and submit as POST instead of GET with URL params
    const formData = new FormData()
    allVisibleProcesses.forEach(row => {
      formData.append('ids[]', row.id)
    })

    // Submit directly to ai_select_message endpoint
    const baseUrl = this.aiSelectButtonTarget.href.split('?')[0];
    fetch(baseUrl, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml'
      }
    })
    .then(response => response.text())
    .then(html => {
      if (html.trim().startsWith("<turbo-stream")) {
        Turbo.renderStreamMessage(html)
        return
      }

      const modalFrame = document.querySelector('turbo-frame[id="modal"]')
      if (modalFrame) {
        modalFrame.innerHTML = html
      }
    })
    .catch(error => {
      console.error('Error loading AI select modal:', error)
      alert('Error loading AI Magic Select. Please try again.')
    })
  }

  bulkDownloadButton(trigger = null) {
    if (trigger) return trigger

    return document.querySelector('[data-resource-preview-bulk-param="download"]')
  }

  downloadAllProcesses(trigger = null) {
    if (this.selectedRowsValue.length === 0) {
      alert('Please select one or more processes to download')
      return
    }

    // Check selection limit
    const maxProcesses = 100
    if (this.selectedRowsValue.length > maxProcesses) {
      alert(`Too many processes selected. Maximum allowed: ${maxProcesses}`)
      return
    }

    console.log('Downloading processes:', this.selectedRowsValue)

    // Show loading indicator
    const downloadButton = this.bulkDownloadButton(trigger)
    const originalHtml = downloadButton?.innerHTML
    const originalClasses = downloadButton?.className

    if (downloadButton) {
      downloadButton.textContent = 'Preparing...'
      downloadButton.disabled = true
      // Apply custom disabled styling that maintains readability
      downloadButton.style.cursor = 'not-allowed'
      downloadButton.style.backgroundColor = 'black'
      downloadButton.style.color = 'white'
    }

    // Create a form for file download
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/processes/download_all'
    form.style.display = 'none'

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    const csrfInput = document.createElement('input')
    csrfInput.type = 'hidden'
    csrfInput.name = 'authenticity_token'
    csrfInput.value = csrfToken
    form.appendChild(csrfInput)

    // Add selected process IDs
    this.selectedRowsValue.forEach(row => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'ids[]'
      input.value = row.id
      form.appendChild(input)
    })

    // Submit the form
    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)

    // Function to reset button state
    const resetButton = () => {
      if (!downloadButton) return

      downloadButton.innerHTML = originalHtml
      downloadButton.disabled = false
      downloadButton.className = originalClasses
      // Reset custom styles
      downloadButton.style.cursor = ''
      downloadButton.style.backgroundColor = ''
      downloadButton.style.color = ''
    }

    // Helper function to get cookie value
    const getCookie = (name) => {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)
      if (parts.length === 2) return parts.pop().split(';').shift()
      return null
    }

    // Poll for download ready cookie
    let pollCount = 0
    const maxPolls = 60 // 30 seconds max (60 * 500ms)

    const pollForDownload = () => {
      pollCount++

      if (getCookie('download_ready') === 'true') {
        // Download is ready, reset button
        resetButton()
        // Clear the cookie
        document.cookie = 'download_ready=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
      } else if (pollCount >= maxPolls) {
        // Timeout after 30 seconds
        resetButton()
      } else {
        // Continue polling
        setTimeout(pollForDownload, 500)
      }
    }

    // Start polling after a brief delay
    setTimeout(pollForDownload, 500)
  }

  viewTasksFromProcesses() {
    if (this.selectedRowsValue.length === 0) {
      alert('Please select one or more processes to review tasks')
      return
    }

    console.log('Reviewing tasks from processes:', this.selectedRowsValue)

    // Build URL with process IDs and inline_actions enabled
    const processIds = this.selectedRowsValue.map(row => row.id).join(',')
    const tasksUrl = `/tasks?process_ids=${encodeURIComponent(processIds)}&inline_actions=true`

    // Navigate to the tasks page
    window.location.href = tasksUrl
  }

  // Route a bulk action from the multi-select preview drawer to the existing
  // bulk flow. The drawer lives in a separate frame, so it dispatches a document
  // event rather than calling these methods directly; selection state is already
  // held here in selectedRowsValue.
  handleBulkAction(event) {
    const action = event.detail?.action
    const references = event.detail?.references || []

    switch (action) {
      case 'state':
        return this.changeStateAllProcesses()
      case 'reassign':
        return this.reassignAllProcesses()
      case 'reply':
        return this.replyAllProcesses()
      case 'tasks':
        return this.viewTasksFromProcesses()
      case 'download':
        return this.downloadAllProcesses(event.detail?.trigger)
      case 'list':
        return this.openAssignListFromPreview({ detail: { references } })
    }
  }

  openAssignList() {
    if (this.selectedRowsValue.length === 0) return

    // Listen for clicks on bulk assign list items in the quick-selector.
    const handler = (e) => {
      const item = e.target.closest('[data-bulk-assign-list-id]')
      if (!item) return
      e.preventDefault()
      document.removeEventListener('click', handler, true)
      const listId = item.dataset.bulkAssignListId
      document.dispatchEvent(new CustomEvent('process-bulk-assign-list:select', { detail: { listId } }))
    }
    document.addEventListener('click', handler, true)

    document.dispatchEvent(new CustomEvent("quick-selector:open", { detail: { id: "process_bulk_assign_list" } }))
  }

  openAssignListFromPreview(event) {
    const references = event.detail?.references || []
    if (references.length === 0) {
      // Drawer is open over the current multi-selection; fall back to it.
      if (this.selectedRowsValue.length > 0) this.openAssignList()
      return
    }

    const referenceSet = new Set(references)
    const rows = this.recordRows()
    const rowsToSelect = rows.filter(row => referenceSet.has(row.getData().reference))

    this.suppressSelectionPreviewRefresh = true
    try {
      this.tabulatorTable.deselectRow()
      this.tabulatorTable.selectRow(rowsToSelect)
    } finally {
      this.suppressSelectionPreviewRefresh = false
    }

    this.selectedRowsValue = this.recordsOnly(this.tabulatorTable.getSelectedData())
    this.openAssignList()
  }

  bulkAssignList(event) {
    const listId = event.detail?.listId
    const references = this.selectedRowsValue.map(row => row.reference)
    if (references.length === 0) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch('/processes/assign_list', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml'
      },
      body: JSON.stringify({ references: references.join(','), list_id: listId || null })
    })
    .then(response => response.text())
    .then(html => {
      if (html.trim().startsWith("<turbo-stream")) {
        Turbo.renderStreamMessage(html)
      }
      try { this.tabulatorTable.replaceData() } catch (e) {}
      this.clearSelection()
    })
    .catch(error => {
      console.error('Error assigning list:', error)
    })
  }
}
